/**
 * Sign Off Modal (Placeholder)
 * =============================
 * Forensic accountant attestation for reconciliation sign-off.
 * TODO: Expand with validation and API integration
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useAccountantAuth } from '../../contexts/AccountantAuthContext';
import api from '../../utils/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface SignOffModalProps {
  reportId: string;
  cellId: string;
  expectedAmount: number;
  actualAmount: number;
  variance: number;
  reclassifiedCount: number;
  onClose: () => void;
  onComplete: () => void;
}

export default function SignOffModal({
  reportId,
  cellId,
  expectedAmount,
  actualAmount,
  variance,
  reclassifiedCount,
  onClose,
  onComplete
}: SignOffModalProps) {
  const { accountant } = useAccountantAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const [signOffMessage, setSignOffMessage] = useState<string | null>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const variancePercent = expectedAmount !== 0 ? ((Math.abs(variance) / Math.abs(expectedAmount)) * 100).toFixed(1) : '0.0';
  const hasSignificantVariance = Math.abs(variance) > 1000 || parseFloat(variancePercent) > 5;

  const handleSignOff = async () => {
    if (!notes.trim() || notes.trim().length < 10) {
      setError('Please provide notes explaining the variance (minimum 10 characters)');
      return;
    }

    if (!accountant) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        `/api/v1/reconciliation/cells/${reportId}/${cellId}/sign-off`,
        {
          notes: notes.trim(),
          variance_accepted: true,
          accountant_name: accountant.full_name,
          accountant_email: accountant.email
        }
      );

      // Success message
      setSignOffMessage(`Reconciliation signed off successfully by ${accountant.full_name}. This attestation has been permanently recorded in the audit trail.`);

      onComplete();
    } catch (err: any) {
      console.error('Error signing off:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to sign off reconciliation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Sign Off Reconciliation" className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">✍️ Sign Off Reconciliation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Line Item */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Line Item</label>
            <p className="text-sm text-gray-900 mt-1">{cellId}</p>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase">Reconciliation Summary</label>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Expected:</span>
              <span className="font-semibold">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(expectedAmount)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Actual:</span>
              <span className="font-semibold">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(actualAmount)}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-gray-300">
              <span className="text-gray-600">Variance:</span>
              <span className={`font-bold ${Math.abs(variance) < 1 ? 'text-gray-900' : variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(variance)}
                <span className="text-sm text-gray-500 ml-2">({variancePercent}%)</span>
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Reclassifications:</span>
              <span className="font-semibold">{reclassifiedCount}</span>
            </div>
          </div>

          {/* Variance Warning */}
          {hasSignificantVariance && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm font-medium text-yellow-900">Significant Variance Detected</p>
                <p className="text-xs text-yellow-700 mt-1">
                  This variance exceeds the normal threshold. Please provide detailed notes explaining the discrepancy.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase block mb-2">
              Notes / Variance Explanation <span className="text-red-600">*</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Document variance explanation, reclassification rationale, investigation findings, or any follow-up needed..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {notes.length}/10 characters minimum
            </p>
          </div>

          {/* Warning */}
          {reclassifiedCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              ⚠️ Reclassifying will update P&L totals and variance calculations immediately.
            </div>
          )}

          {/* Accountant Info */}
          {accountant && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-2">
                Signing Off As
              </label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                  {accountant.full_name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{accountant.full_name}</p>
                  <p className="text-xs text-gray-500">{accountant.email}</p>
                  {accountant.firm_name && (
                    <p className="text-xs text-gray-500">{accountant.firm_name}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSignOff}
            disabled={loading || notes.trim().length < 10}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing Off...' : '✓ Confirm Sign-Off'}
          </button>
        </div>
      </div>
    </div>
  );
}
